import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Loader2, ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import Logo from '../components/Logo';

const AuthPage = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nom: '',
    entreprise: '',
    adresse: '',
    siret: '',
    iban: '',
  });
  const [forgotEmail, setForgotEmail] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await api.post(endpoint, payload);
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success(isLogin ? 'Connexion réussie !' : 'Compte créé avec succès !');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error('Veuillez entrer votre adresse email');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      setForgotPasswordSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const resetForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotPasswordSent(false);
    setForgotEmail('');
  };

  // Formulaire mot de passe oublié
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-slate-50 flex items-center justify-center p-4">
        <Card className="bg-white border border-slate-100 shadow-xl rounded-xl p-8 w-full max-w-md">
          <div className="space-y-6">
            {forgotPasswordSent ? (
              // Confirmation d'envoi
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-600" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Email envoyé !
                </h3>
                <p className="text-slate-600 mb-6">
                  Si l'adresse <span className="font-medium">{forgotEmail}</span> est associée à un compte, 
                  vous recevrez un email avec les instructions pour réinitialiser votre mot de passe.
                </p>
                <p className="text-sm text-slate-500 mb-6">
                  N'oubliez pas de vérifier vos spams.
                </p>
                <Button
                  onClick={resetForgotPassword}
                  className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-lg py-5"
                  data-testid="back-to-login-btn"
                >
                  Retour à la connexion
                </Button>
              </div>
            ) : (
              // Formulaire de demande
              <>
                <button
                  onClick={resetForgotPassword}
                  className="flex items-center text-sm text-slate-600 hover:text-rose-600 transition-colors"
                  data-testid="back-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Retour
                </button>
                
                <div className="text-center">
                  <div className="w-14 h-14 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-7 h-7 text-rose-600" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Mot de passe oublié ?
                  </h3>
                  <p className="text-sm text-slate-500">
                    Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
                  </p>
                </div>

                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <Label htmlFor="forgot-email" className="text-slate-700">Adresse email</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      placeholder="votre@email.com"
                      data-testid="forgot-email-input"
                      className="border-slate-200 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all rounded-md mt-1"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                    className="w-full bg-rose-600 hover:bg-rose-700 text-white shadow-sm transition-all rounded-lg py-5"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Envoi en cours...</>
                    ) : (
                      'Envoyer le lien de réinitialisation'
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </Card>
      </div>
    );
  }

  // Formulaire de connexion / inscription standard
  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        {/* Left: Branding */}
        <div className="hidden lg:block space-y-6 animate-fade-in">
          <div className="flex items-center">
            <Logo variant="full" size="h-20" />
          </div>
          <h2 className="text-3xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Gérez vos options premium en toute simplicité
          </h2>
          <p className="text-lg text-slate-600">
            Vendez des expériences exceptionnelles à vos voyageurs et augmentez vos revenus.
          </p>
        </div>

        {/* Right: Form */}
        <Card className="bg-white border border-slate-100 shadow-xl rounded-xl p-8">
          <div className="space-y-6">
            <div className="text-center lg:text-left">
              <h3 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {isLogin ? 'Connexion' : 'Créer un compte'}
              </h3>
              <p className="text-sm text-slate-500">
                {isLogin ? 'Bon retour parmi nous !' : 'Commencez à maximiser vos revenus'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="auth-email-input"
                  className="border-slate-200 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all rounded-md mt-1"
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
                  {isLogin && (
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      data-testid="forgot-password-link"
                      className="text-xs text-rose-600 hover:text-rose-700 font-medium"
                    >
                      Mot de passe oublié ?
                    </button>
                  )}
                </div>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="auth-password-input"
                  className="border-slate-200 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all rounded-md mt-1"
                />
              </div>

              {!isLogin && (
                <>
                  <div>
                    <Label htmlFor="nom" className="text-slate-700">Nom complet</Label>
                    <Input
                      id="nom"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      data-testid="auth-nom-input"
                      className="border-slate-200 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all rounded-md mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="entreprise" className="text-slate-700">Entreprise</Label>
                    <Input
                      id="entreprise"
                      name="entreprise"
                      value={formData.entreprise}
                      onChange={handleChange}
                      required
                      data-testid="auth-entreprise-input"
                      className="border-slate-200 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all rounded-md mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="adresse" className="text-slate-700">Adresse</Label>
                    <Input
                      id="adresse"
                      name="adresse"
                      value={formData.adresse}
                      onChange={handleChange}
                      required
                      data-testid="auth-adresse-input"
                      className="border-slate-200 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all rounded-md mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="siret" className="text-slate-700">SIRET</Label>
                      <Input
                        id="siret"
                        name="siret"
                        value={formData.siret}
                        onChange={handleChange}
                        required
                        data-testid="auth-siret-input"
                        className="border-slate-200 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all rounded-md mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="iban" className="text-slate-700">IBAN</Label>
                      <Input
                        id="iban"
                        name="iban"
                        value={formData.iban}
                        onChange={handleChange}
                        required
                        data-testid="auth-iban-input"
                        className="border-slate-200 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all rounded-md mt-1"
                      />
                    </div>
                  </div>
                </>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="auth-submit-btn"
                className="w-full bg-rose-600 hover:bg-rose-700 text-white shadow-sm transition-all rounded-lg py-5"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Chargement...</>
                ) : (
                  isLogin ? 'Se connecter' : 'Créer mon compte'
                )}
              </Button>
            </form>

            <div className="text-center">
              <button
                onClick={() => setIsLogin(!isLogin)}
                data-testid="auth-toggle-btn"
                className="text-sm text-rose-600 hover:text-rose-700 font-medium"
              >
                {isLogin ? 'Pas encore de compte ? S\'inscrire' : 'Déjà un compte ? Se connecter'}
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AuthPage;
